import { useState } from 'react';

import { withForm } from '@/components/form';
import {
  AddCustomField,
  Checkbox,
  EditableField,
  FieldGrid,
  Icon,
  SectionCard,
  type EditableFieldType,
} from '@/components/ui';
import { cn } from '@/utils/cn';

import { defaultFormValues } from '../../contactSchema';

interface CustomField {
  name: string;
  type: EditableFieldType;
  value: string;
}

const STATUS_OPTIONS: { value: 'active' | 'inactive'; label: string; tone: 'green' | 'slate' }[] = [
  { value: 'active', label: 'Active', tone: 'green' },
  { value: 'inactive', label: 'Inactive', tone: 'slate' },
];

export const StatusSection = withForm({
  defaultValues: defaultFormValues,
  render: function StatusSection({ form }) {
    const [customFields, setCustomFields] = useState<CustomField[]>([]);

    return (
      <SectionCard title="Status & preferences">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <form.Subscribe selector={(s) => s.values.status?.active}>
            {(active) => {
              const current: 'active' | 'inactive' = active ? 'active' : 'inactive';
              return (
                <div>
                  <SubLabel>Status</SubLabel>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={cn('chip-btn', current === opt.value && 'active')}
                        onClick={() => form.setFieldValue('status.active', opt.value === 'active')}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: `var(--${opt.tone})`,
                          }}
                        />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }}
          </form.Subscribe>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <form.AppField name="status.doNotContact">
              {(field) => (
                <ToggleRow
                  label="Do not contact"
                  description="Excluded from every outreach, independent of the status"
                  icon={<Icon name="x" size={13} />}
                  checked={!!field.state.value}
                  onChange={(v) => field.handleChange(v)}
                />
              )}
            </form.AppField>
            <form.AppField name="status.verified">
              {(field) => (
                <ToggleRow
                  label="Verified"
                  description="Identity confirmed by a trusted source"
                  icon={<Icon name="shield" size={13} />}
                  checked={!!field.state.value}
                  onChange={(v) => field.handleChange(v)}
                />
              )}
            </form.AppField>
            <form.AppField name="status.vip">
              {(field) => (
                <ToggleRow
                  label="VIP"
                  description="High-priority contact for outreach"
                  icon={<Icon name="star" size={13} />}
                  checked={!!field.state.value}
                  onChange={(v) => field.handleChange(v)}
                />
              )}
            </form.AppField>
          </div>
        </div>

        <FieldGrid>
          <form.AppField name="status.reason">
            {(field) => (
              <EditableField
                label="Reason / notes"
                type="textarea"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          {customFields.map((cf) => (
            <EditableField
              key={cf.name}
              label={cf.name}
              type={cf.type}
              custom
              value={cf.value}
              display={cf.value || '—'}
              onSave={(v) =>
                setCustomFields((prev) =>
                  prev.map((f) => (f.name === cf.name ? { ...f, value: v } : f)),
                )
              }
            />
          ))}
        </FieldGrid>

        <AddCustomField
          section="Status"
          onAdd={(name, type) => setCustomFields((prev) => [...prev, { name, type, value: '' }])}
        />
      </SectionCard>
    );
  },
});

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: 'var(--text-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 6,
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

function ToggleRow({
  label,
  description,
  icon,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  icon: React.ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      style={{
        flex: '1 1 220px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-2)',
      }}
    >
      <span style={{ color: 'var(--text-3)' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{description}</div>
      </div>
      <Checkbox checked={checked} onChange={onChange} aria-label={label} />
    </div>
  );
}
